import { Controller } from "@hotwired/stimulus"
import { autocomplete } from '@algolia/autocomplete-js'

const MIN_QUERY_LENGTH = 2
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

// Connects to data-controller="public--hero-search"
//
// The hero's search and the snapshot it drives. The snapshot is the
// server-rendered turbo-frame#market-preview inside the snapshot target; a
// search sets its src to the market's hero fragment and Turbo swaps its
// children, so the frame's own events bubble up to that target.
export default class extends Controller {
  static targets = ['input', 'status', 'snapshot', 'disclosure']
  static values = { defaultMarketUuid: String, defaultMarketName: String, defaultSource: String }

  connect() {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return
    this.market = this.hasDefaultMarketUuidValue
      ? { uuid: this.defaultMarketUuidValue, name: this.defaultMarketNameValue }
      : null
    this.chartElements = null
    if (this.market) {
      this.measurement = {
        source: this.defaultSourceValue, market_uuid: this.market.uuid, startedAt: 0,
        ms_to_html: Math.round(performance.getEntriesByType('navigation')[0].responseEnd),
      }
    }
    this.initAutocomplete()
  }

  pageLoaded({ detail: { timing } }) {
    if (!this.measurement || !this.measurement.source.startsWith('default_') || !timing.visitStart) return
    this.measurement.startedAt = timing.visitStart - performance.timeOrigin
    this.measurement.ms_to_html = Math.round((timing.requestEnd ?? Date.now()) - timing.visitStart)
  }

  initAutocomplete() {
    if (this.autocompleteInstance) {
      this.autocompleteInstance.destroy()
      this.autocompleteInstance = null
    }

    // The static field the server rendered for no-JS visitors.
    this.inputTarget.replaceChildren()

    this.autocompleteInstance = autocomplete({
      container: this.inputTarget,
      detachedMediaQuery: 'none',
      placeholder: "Search any market",
      initialState: { query: this.market?.name ?? '' },
      openOnFocus: false,
      classNames: { panel: 'hero-search-panel' },
      getInputProps: ({ props }) => ({
        ...props,
        'aria-labelledby': undefined,
        'aria-label': 'ZIP code, city, or neighborhood',
        'aria-describedby': this.statusTarget.id,
      }),
      translations: { submitButtonTitle: 'Search markets' },
      getSources: ({ query }) => this.getSource(query),
      onSubmit: ({ state }) => this.openBestMatch(state.query),
      onReset: () => {
        this.searchQuery = ''
        this.setStatus('Type your neighborhood, ZIP, or city.')
      },
      navigator: {
        // onSelect handles the inline snapshot; suppress Algolia's page navigation.
        navigate: () => {},
      },
    })
  }

  // The idle loop clears each name it has typed and searched, leaving the
  // field empty for the visitor's own.
  clearQuery() {
    this.autocompleteInstance.setQuery('')
  }

  async openBestMatch(query) {
    query = query.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      this.setStatus('Type two or more characters.')
      return
    }

    const results = await this.fetchMarkets(query)
    if (query === this.searchQuery && results.length) {
      this.selectMarket(results[0], 'submit')
    }
  }

  async fetchMarkets(query) {
    this.searchQuery = query
    this.setStatus(`Finding “${query}”…`, true)

    try {
      const response = await fetch(`/us/markets.json?q=${encodeURIComponent(query)}`)
      if (!response.ok) throw new Error(`Market search failed (${response.status})`)

      const { results } = await response.json()
      if (query === this.searchQuery) {
        this.resultCount = results.length
        if (!results.length) window.posthog?.capture('www.market_search_empty', { query_length: query.length })
        this.setStatus(results.length
          ? (results.length === 1 ? '1 market found.' : `${results.length} markets found. Pick one.`)
          : `No market named “${query}”. Try the ZIP code or the nearest city.`)
      }
      return results
    } catch (error) {
      if (query === this.searchQuery) {
        this.setStatus('The search didn’t go through. Try again.')
      }
      throw error
    }
  }

  getSource(query) {
    query = query.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      this.searchQuery = query
      this.setStatus('Type two or more characters.')
      return []
    }

    return this.fetchMarkets(query)
      .then(results => {
        const grouped = results.reduce((acc, item) => {
          const type = item.type || 'Other'
          acc[type] = acc[type] || []
          acc[type].push(item)
          return acc
        }, {})

        return Object.entries(grouped).map(([type, items]) => ({
          sourceId: type,
          getItems: () => items,
          getItemInputValue: ({ item }) => item.name,
          getItemUrl: ({ item }) => `/us/markets/${item.market_uuid}`,
          onSelect: ({ item }) => this.selectMarket(item, 'select'),
          templates: {
            header: ({ html }) => html`<span class="aa-SourceHeaderTitle">${type}</span><div class="aa-SourceHeaderLine"></div>`,
            item: ({ item, html }) => html`
              <div class="aa-ItemWrapper">
                <div class="aa-ItemContent">
                  <div class="aa-ItemContentBody">
                    <div class="aa-ItemContentTitle">${item.name}</div>
                    <div class="aa-ItemContentDescription">${item.common_region_name}</div>
                  </div>
                </div>
              </div>`,
          },
        }))
      })
  }

  selectMarket(item, method) {
    window.posthog?.capture('www.market_search_submitted', {
      method,
      query_length: this.searchQuery.length,
      result_count: this.resultCount,
      market_type: item.type,
      market_uuid: item.market_uuid,
    })
    this.showMarket(item.market_uuid, item.name)
  }

  showMarket(uuid, name) {
    this.market = { uuid, name }
    this.autocompleteInstance.setIsOpen(false)
    this.renderStartedAt = performance.now()
    this.chartElements = new Set()
    this.measurement = { source: 'searched', market_uuid: uuid, startedAt: this.renderStartedAt }
    this.setStatus(`Finding ${name}…`, true)

    const frame = this.frame
    frame.removeAttribute('src')
    frame.src = `/us/markets/${uuid}/hero`

    this.disclosureTarget.href = this.campaignTagged(`/us/markets/${uuid}`)
    this.disclosureTarget.textContent = `Show the full snapshot for ${name}`
    this.disclosureTarget.hidden = false
  }

  snapshotDrawing(event) {
    this.chartElements = new Set(event.detail.newFrame.querySelectorAll('*'))
    if (this.renderStartedAt) this.setStatus(`Drawing ${this.market.name}…`, true)
  }

  snapshotLoaded() {
    if (this.measurement) {
      this.measurement.ms_to_html = Math.round(performance.now() - this.measurement.startedAt)
      this.captureRender()
    }
    this.setStatus(`Market snapshot · ${this.market.name}`)
    if (!this.renderStartedAt) return

    const ms = Math.round(performance.now() - this.renderStartedAt)
    this.renderStartedAt = null
    this.dispatch('rendered', { detail: { ms, ...this.market } })
    this.renameClose()
  }

  // "Try again" is the submit control: it searches the field's market and
  // sets the frame's src afresh.
  snapshotFailed(event) {
    this.measurement = null
    window.posthog?.capture('www.market_preview_failed', {
      reason: event.type,
      status: event.detail?.response?.status ?? null,
    })
    this.renderStartedAt = null
    this.setStatus(`${this.market.name} didn’t load. Try again.`)

    if (event.type === 'turbo:frame-missing') {
      event.preventDefault()
      throw new Error(`Market snapshot response is missing its frame (${event.detail.response.status})`)
    }
  }

  loopLoading(event) {
    this.chartElements = new Set()
    this.measurement = { source: 'loop', market_uuid: event.detail.uuid, startedAt: performance.now() }
  }

  chartFinished(event) {
    if (this.chartElements && !this.chartElements.has(event.target)) return
    if (!this.measurement || this.measurement.ms_to_first_chart !== undefined) return
    this.measurement.ms_to_first_chart = Math.round(performance.now() - this.measurement.startedAt)
    this.captureRender()
  }

  captureRender() {
    if (this.measurement.ms_to_html === undefined || this.measurement.ms_to_first_chart === undefined) return
    const { startedAt, ...properties } = this.measurement
    window.posthog?.capture('www.market_preview_rendered', properties)
    this.measurement = null
  }

  searchFocused(event) {
    if (!this.inputTarget.contains(event.target)) return
    window.posthog?.capture('www.market_search_focus', { via: this.loopTakeover ? 'loop_takeover' : 'click' })
    this.loopTakeover = false
  }

  loopStopped() {
    this.loopTakeover = true
  }

  disclosureOpened() {
    window.posthog?.capture('www.snapshot_disclosure_opened', { market_uuid: this.market.uuid }, { transport: 'sendBeacon' })
  }

  // The close names the hero's last-rendered market (PLAN §4, G).
  renameClose() {
    document.getElementById('close-headline').textContent = `Make ${this.market.name} yours.`
  }

  campaignTagged(path) {
    const url = new URL(path, window.location.origin)
    const query = new URLSearchParams(window.location.search)
    for (const key of UTM_PARAMS) {
      if (query.has(key)) url.searchParams.set(key, query.get(key))
    }
    return url.pathname + url.search
  }

  get frame() {
    return this.snapshotTarget.querySelector('turbo-frame')
  }

  // An unchanged caption is left alone: rewriting the live region's text
  // makes it announce again, and the idle loop's swaps land here every pass.
  setStatus(message, busy = false) {
    if (this.statusTarget.textContent !== message) this.statusTarget.textContent = message
    this.inputTarget.setAttribute('aria-busy', String(busy))
  }

  disconnect() {
    if (this.autocompleteInstance) {
      this.autocompleteInstance.destroy()
      this.autocompleteInstance = null
    }
  }
}
